import type { Network } from '@tol/core/types';
import { networkRegions } from '@tol/ui/network-view';
import { mergeNetworks } from './namespace.ts';

// Keep three recently drawn cities' networks and station sources. Active cities are never
// evicted: a wide viewport may need more, but visiting cities cannot grow it.
export function retainNetworks(
  send: (active: Network, rendered: Network) => void,
  evict?: (id: string) => void,
): (shown: ReadonlyMap<string, Network>, available?: ReadonlyMap<string, Network>) => void {
  const recent = new Map<string, Network>();
  let rendered = mergeNetworks([]);
  let active = new Map<string, Network>();
  return (shown, available = shown) => {
    const sameActive =
      shown.size === active.size && [...shown].every(([id, net]: readonly [string, Network]) => active.get(id) === net);
    active = new Map(shown);
    const previous = new Map(recent);
    // Include downloads that finished after the user moved away: they must
    // not accumulate outside the visible-network cache.
    for (const [id, net] of available) recent.set(id, net);
    for (const [id, net] of shown) {
      recent.delete(id);
      recent.set(id, net);
    }
    const evicted: string[] = [];
    for (const id of recent.keys()) {
      if (recent.size <= Math.max(3, shown.size)) break;
      if (!shown.has(id)) {
        recent.delete(id);
        evicted.push(id);
      }
    }
    const changed =
      recent.size !== previous.size ||
      [...recent].some(([id, net]: readonly [string, Network]) => previous.get(id) !== net);
    if (changed) {
      rendered = mergeNetworks([...recent.values()]);
    }
    if (!sameActive || changed) {
      const net = mergeNetworks([...shown.values()]);
      networkRegions.set(net, [...shown.keys()].toSorted());
      send(net, rendered);
    }
    for (const id of evicted) evict?.(id);
  };
}
